import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useJWTAuth } from './JWTAuthContext';
import { getUserPointSummary } from './apiClient';

const NavBar: React.FC = () => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isAuthenticated, user, logout } = useJWTAuth();
  const [pointData, setPointData] = useState<{
    available_points: number;
    ai_minutes: number;
  } | null>(null);

  useEffect(() => {
    const fetchPointData = async () => {
      if (isAuthenticated && user && user.id) {
        try {
          const response = await getUserPointSummary(user.id);
          if (response && response.status === 'success' && response.result) {
            setPointData({
              available_points: response.result.points.available_points,
              ai_minutes: Math.floor(response.result.ai_tutor.minutes_available)
            });
          }
        } catch (error) {
          console.error('Failed to fetch point data for navbar:', error);
        }
      }
    };

    fetchPointData();
    // Refresh data every 30 seconds when user is authenticated
    const interval = null;
    //const interval = isAuthenticated ? setInterval(fetchPointData, 30000) : null;
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAuthenticated, user]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    setIsMenuOpen(false);
  };

  return (
    <div className="navbar-fixed-top">
      <nav className="navbar navbar-expand-lg navbar-light bg-white shadow-sm py-3">
        <div className="container">
          <Link to="/" className="navbar-brand fw-bold d-flex align-items-center" onClick={closeMenu}>
            <div className="d-inline-flex align-items-center justify-content-center rounded-circle me-2"
              style={{ 
                width: '40px', 
                height: '40px',
                background: 'linear-gradient(135deg, #667eea, #764ba2)'
              }}>
              <img src="/logo.png" alt="wiziai Logo" style={{ width: '40px', height: '40px' }} />
            </div>
            <span className="brand" style={{ 
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              wiziai
            </span>
          </Link>
          
          <button 
            className="navbar-toggler border-0 shadow-none" 
            type="button" 
            onClick={toggleMenu}
            style={{ padding: '8px 12px' }}>
            <div className="d-flex flex-column" style={{ width: '20px', height: '16px' }}>
              <span className={`bg-primary rounded-pill transition-all ${isMenuOpen ? 'rotate-45 translate-y-1' : ''}`} 
                style={{ height: '2px', width: '100%', marginBottom: '3px', transition: 'all 0.3s ease' }}></span>
              <span className={`bg-primary rounded-pill transition-all ${isMenuOpen ? 'opacity-0' : ''}`} 
                style={{ height: '2px', width: '100%', marginBottom: '3px', transition: 'all 0.3s ease' }}></span>
              <span className={`bg-primary rounded-pill transition-all ${isMenuOpen ? '-rotate-45 -translate-y-1' : ''}`} 
                style={{ height: '2px', width: '100%', transition: 'all 0.3s ease' }}></span>
            </div>
          </button>

          <div className={`collapse navbar-collapse ${isMenuOpen ? 'show' : ''}`} id="navbarCollapse"
               style={{ transition: 'all 0.3s ease' }}>
            <div className="navbar-nav ms-auto">
              <Link
                to="/"
                className={`nav-link px-3 py-2 mx-1 rounded-pill fw-medium position-relative ${
                  location.pathname === '/' ? 'text-primary' : 'text-dark'
                }`}
                onClick={closeMenu}
                style={{ 
                  transition: 'all 0.3s ease',
                  backgroundColor: location.pathname === '/' ? '#667eea15' : 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (location.pathname !== '/') {
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                    e.currentTarget.style.color = '#667eea';
                  }
                }}
                onMouseLeave={(e) => {
                  if (location.pathname !== '/') {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#212529';
                  }
                }}>
                <i className="fas fa-home me-2"></i>Home
              </Link>
              
              {!isAuthenticated && (
                <>
                  <Link
                    to="/about"
                    className={`nav-link px-3 py-2 mx-1 rounded-pill fw-medium position-relative ${
                      location.pathname === '/about' ? 'text-primary' : 'text-dark'
                    }`}
                    onClick={closeMenu}
                    style={{ 
                      transition: 'all 0.3s ease',
                      backgroundColor: location.pathname === '/about' ? '#667eea15' : 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      if (location.pathname !== '/about') {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                        e.currentTarget.style.color = '#667eea';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (location.pathname !== '/about') {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#212529';
                      }
                    }}>
                    <i className="fas fa-info-circle me-2"></i>About
                  </Link>
              
                
                  <Link
                    to="/contact"
                    className={`nav-link px-3 py-2 mx-1 rounded-pill fw-medium position-relative ${
                      location.pathname === '/contact' ? 'text-primary' : 'text-dark'
                    }`}
                    onClick={closeMenu}
                    style={{ 
                      transition: 'all 0.3s ease',
                      backgroundColor: location.pathname === '/contact' ? '#667eea15' : 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      if (location.pathname !== '/contact') {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                        e.currentTarget.style.color = '#667eea';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (location.pathname !== '/contact') {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#212529';
                      }
                    }}>
                    <i className="fas fa-envelope me-2"></i>Contact
                  </Link>
                </>
              )}

            <Link
              to="/pricing"
              className={`nav-link px-3 py-2 mx-1 rounded-pill fw-medium position-relative ${
                location.pathname === '/pricing' ? 'text-primary' : 'text-dark'
              }`}
              onClick={closeMenu}
              style={{ 
                transition: 'all 0.3s ease',
                backgroundColor: location.pathname === '/pricing' ? '#667eea15' : 'transparent'
              }}
              onMouseEnter={(e) => {
                if (location.pathname !== '/pricing') {
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                  e.currentTarget.style.color = '#667eea';
                }
              }}
              onMouseLeave={(e) => {
                if (location.pathname !== '/pricing') {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#212529';
                }
              }}>
              <i className="fas fa-tag me-2"></i>Pricing
            </Link>
            
            <div className="d-none d-lg-block ms-3">
              {isAuthenticated ? (
                // Authenticated user menu
                <div className="d-flex align-items-center">
                  {/* Points and AI Minutes Display */}
                  {pointData && (
                    <div className="d-flex align-items-center me-3">
                      <div className="d-flex align-items-center me-3 px-3 py-2 rounded-pill shadow-sm"
                           style={{ 
                             background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
                             border: '2px solid #ff9800'
                           }}>
                        <i className="fas fa-coins text-warning me-2" style={{ fontSize: '1.1rem' }}></i>
                        <span className="fw-bold text-dark me-1" style={{ fontSize: '0.9rem' }}>
                          {pointData.available_points.toLocaleString()}
                        </span>
                        <small className="text-dark fw-medium">pts</small>
                      </div>
                      <div className="d-flex align-items-center me-3 px-3 py-2 rounded-pill shadow-sm"
                           style={{ 
                             background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                             border: '2px solid #17a2b8'
                           }}>
                        <i className="fas fa-robot text-info me-2" style={{ fontSize: '1.1rem' }}></i>
                        <span className="fw-bold text-dark me-1" style={{ fontSize: '0.9rem' }}>
                          {pointData.ai_minutes}
                        </span>
                        <small className="text-dark fw-medium">min</small>
                      </div>
                    </div>
                  )}
                  
                  <Link
                    to="/dashboard"
                    className="btn btn-outline-primary me-2 px-4"
                    onClick={closeMenu}
                    style={{ 
                      borderRadius: '25px',
                      transition: 'all 0.3s ease'
                    }}>
                    <i className="fas fa-tachometer-alt me-1"></i>
                    Dashboard
                  </Link>
                  <div className="dropdown">
                    <button
                      className="btn px-4 dropdown-toggle"
                      type="button"
                      data-bs-toggle="dropdown"
                      style={{ 
                        background: 'linear-gradient(135deg, #667eea, #764ba2)',
                        border: 'none',
                        borderRadius: '25px',
                        color: 'white'
                      }}>
                      <i className="fas fa-user-circle me-1"></i>
                      {user?.display_name || user?.username || 'User'}
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end" style={{ borderRadius: '15px' }}>
                      <li>
                        <Link className="dropdown-item" to="/profile" onClick={closeMenu}>
                          <i className="fas fa-user me-2"></i>Profile
                        </Link>
                      </li>
                      <li>
                        <Link className="dropdown-item" to="/performance" onClick={closeMenu}>
                          <i className="fas fa-chart-line me-2"></i>Performance
                        </Link>
                      </li>
                      <li>
                        <Link className="dropdown-item" to="/report-card" onClick={closeMenu}>
                          <i className="fas fa-trophy me-2"></i>Report Card
                        </Link>
                      </li>
                      <li>
                        <Link className="dropdown-item" to="/mock-test" onClick={closeMenu}>
                          <i className="fas fa-edit me-2"></i>Mock Tests
                        </Link>
                      </li>
                      <li>
                        <Link className="dropdown-item" to="/pyq-history" onClick={closeMenu}>
                          <i className="fas fa-archive me-2"></i>PYQ History
                        </Link>
                      </li>
                      <li>
                        <Link className="dropdown-item" to="/custom-quiz-history" onClick={closeMenu}>
                          <i className="fas fa-cog me-2"></i>Custom Quiz History
                        </Link>
                      </li>
                      <li>
                        <Link className="dropdown-item" to="/ai-tutor" onClick={closeMenu}>
                          <i className="fas fa-robot me-2"></i>AI Tutor
                        </Link>
                      </li>
                      <li>
                        <Link className="dropdown-item" to="/pricing" onClick={closeMenu}>
                          <i className="fas fa-tag me-2"></i>Pricing
                        </Link>
                      </li>
                      <li><hr className="dropdown-divider" /></li>
                      <li>
                        <button className="dropdown-item" onClick={handleLogout}>
                          <i className="fas fa-sign-out-alt me-2"></i>Logout
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              ) : (
                // Non-authenticated user menu
                <div>
                  <Link
                    to="/login"
                    className="btn btn-outline-primary me-2 px-4"
                    onClick={closeMenu}
                    style={{ 
                      borderRadius: '25px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(102, 126, 234, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}>
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    className="btn px-4"
                    onClick={closeMenu}
                    style={{ 
                      background: 'linear-gradient(135deg, #667eea, #764ba2)',
                      border: 'none',
                      borderRadius: '25px',
                      color: 'white',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 16px rgba(102, 126, 234, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}>
                    Get Started
                  </Link>
                </div>
              )}
            </div>
            
            <div className="d-lg-none mt-3">
              {isAuthenticated ? (
                // Mobile authenticated menu
                <div>
                  {/* Mobile Points Display */}
                  {pointData && (
                    <div className="row g-2 mb-3">
                      <div className="col-6">
                        <div className="d-flex align-items-center justify-content-center p-3 rounded shadow-sm"
                             style={{ 
                               background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
                               border: '2px solid #ff9800'
                             }}>
                          <i className="fas fa-coins text-warning me-2" style={{ fontSize: '1.2rem' }}></i>
                          <span className="fw-bold text-dark me-1">{pointData.available_points.toLocaleString()}</span>
                          <small className="text-dark fw-medium">pts</small>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="d-flex align-items-center justify-content-center p-3 rounded shadow-sm"
                             style={{ 
                               background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                               border: '2px solid #17a2b8'
                             }}>
                          <i className="fas fa-robot text-info me-2" style={{ fontSize: '1.2rem' }}></i>
                          <span className="fw-bold text-dark me-1">{pointData.ai_minutes}</span>
                          <small className="text-dark fw-medium">min</small>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <Link
                    to="/dashboard"
                    className="btn btn-outline-primary w-100 mb-2"
                    onClick={closeMenu}
                    style={{ borderRadius: '25px' }}>
                    <i className="fas fa-tachometer-alt me-1"></i>
                    Dashboard
                  </Link>
                  <Link
                    to="/profile"
                    className="btn btn-outline-secondary w-100 mb-2"
                    onClick={closeMenu}
                    style={{ borderRadius: '25px' }}>
                    <i className="fas fa-user me-1"></i>
                    Profile
                  </Link>
                  <Link
                    to="/performance"
                    className="btn btn-outline-info w-100 mb-2"
                    onClick={closeMenu}
                    style={{ borderRadius: '25px' }}>
                    <i className="fas fa-chart-line me-1"></i>
                    Performance
                  </Link>
                  <Link
                    to="/report-card"
                    className="btn btn-outline-success w-100 mb-2"
                    onClick={closeMenu}
                    style={{ borderRadius: '25px' }}>
                    <i className="fas fa-trophy me-1"></i>
                    Report Card
                  </Link>
                  <Link
                    to="/mock-test"
                    className="btn btn-outline-warning w-100 mb-2"
                    onClick={closeMenu}
                    style={{ borderRadius: '25px' }}>
                    <i className="fas fa-edit me-1"></i>
                    Mock Tests
                  </Link>
                  <Link
                    to="/pyq-history"
                    className="btn btn-outline-purple w-100 mb-2"
                    onClick={closeMenu}
                    style={{ borderRadius: '25px', borderColor: '#6f42c1', color: '#6f42c1' }}>
                    <i className="fas fa-archive me-1"></i>
                    PYQ History
                  </Link>
                  <Link
                    to="/custom-quiz-history"
                    className="btn btn-outline-info w-100 mb-2"
                    onClick={closeMenu}
                    style={{ borderRadius: '25px' }}>
                    <i className="fas fa-cog me-1"></i>
                    Custom Quiz History
                  </Link>
                  <Link
                    to="/ai-tutor"
                    className="btn btn-outline-info w-100 mb-2"
                    onClick={closeMenu}
                    style={{ borderRadius: '25px' }}>
                    <i className="fas fa-robot me-1"></i>
                    AI Tutor
                  </Link>
                  <Link
                    to="/pricing"
                    className="btn btn-outline-primary w-100 mb-2"
                    onClick={closeMenu}
                    style={{ borderRadius: '25px' }}>
                    <i className="fas fa-tag me-1"></i>
                    Pricing
                  </Link>
                  <button
                    className="btn btn-outline-danger w-100"
                    onClick={handleLogout}
                    style={{ borderRadius: '25px' }}>
                    <i className="fas fa-sign-out-alt me-1"></i>
                    Logout
                  </button>
                </div>
              ) : (
                // Mobile non-authenticated menu
                <div>
                  <Link
                    to="/login"
                    className="btn btn-outline-primary w-100 mb-2"
                    onClick={closeMenu}
                    style={{ borderRadius: '25px' }}>
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    className="btn w-100"
                    onClick={closeMenu}
                    style={{ 
                      background: 'linear-gradient(135deg, #667eea, #764ba2)',
                      border: 'none',
                      borderRadius: '25px',
                      color: 'white'
                    }}>
                    Get Started
                  </Link>
                </div>
              )}
            </div>
          </div>
          </div>
        </div>
      </nav>
    </div>
  );
};

export default NavBar;
